import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { Box, Button, Chip, IconButton, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { Add, AttachFileOutlined, VisibilityOutlined } from '@mui/icons-material'
import moment from 'moment'
import gaiaTheme from '../../../../root-components/theme'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model'
import { TableDisplay } from '../../miscellaneous-components/table-display'
import { TableActionButtons } from '../../miscellaneous-components/table-action-buttons'
import { LoadingSpinner } from '../../display-elements-components/loading-spinner'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import ManagementSystemCertificationService from '../../../../shared/services/management-system-certification/management-system-certification.service'
import {
  CertificationHolderModelName,
  ICertificationCatalog,
  IManagementSystemCertification,
  IPostManagementSystemCertification,
} from '../../../../shared/services/management-system-certification/management-system-certification.model'
import { certificationStatusChip } from '../regulation-status'
import { AddEditCertificationDialog } from './add-edit-certification-dialog'
import { CertificationDocumentsDialog } from './certification-documents-dialog'

const formatDate = (isoDate: string | null): string => (isoDate ? moment(isoDate, 'YYYY-MM-DD').format('DD/MM/YYYY') : '')
const formatLongDate = (isoDate: string): string => moment(isoDate, 'YYYY-MM-DD').format('D MMMM YYYY')

/**
 * The management-system certificates (ISO 14001, ISO 50001) one holder has recorded: the
 * organisation itself (Regulations page, Certifications tab) or a supplier (supplier page). Status,
 * effective expiry and transition facts come from the backend's `derived` so every surface agrees.
 */
export const CertificationsPanel: FunctionComponent<{
  holderModelName: CertificationHolderModelName,
  holderObjectId: string,
  canEdit: boolean,
  onChanged?: () => void,
}> = ({ holderModelName, holderObjectId, canEdit, onChanged }) => {
  const service = new ManagementSystemCertificationService()
  const userNotificationsContext = useContext(UserNotificationsContext)
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'))
  const isOrganisation = holderModelName === 'organisation'

  const [catalog, setCatalog] = useState<ICertificationCatalog | null>(null)
  const [rows, setRows] = useState<IManagementSystemCertification[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [selected, setSelected] = useState<string[]>([])
  const [perPage, setPerPage] = useState(25)
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [editing, setEditing] = useState<IManagementSystemCertification | null>(null)
  const [documentsFor, setDocumentsFor] = useState<IManagementSystemCertification | null>(null)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const load = () => {
    setErrorMessage('')
    return service.getAll({ holderModelName, holderObjectId })
      .then((response) => setRows(response))
      .catch((err) => {
        console.error(err)
        setErrorMessage('The certificates could not be loaded. Please try again.')
      })
  }

  useEffect(() => {
    let isActive = true
    setIsLoading(true)
    Promise.all([
      service.getCatalog().then((response) => { if (isActive) setCatalog(response) }).catch((err) => console.error(err)),
      load(),
    ]).finally(() => { if (isActive) setIsLoading(false) })
    return () => { isActive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holderModelName, holderObjectId])

  const openAdd = () => { setEditing(null); setDialogOpen(true) }
  const openEdit = (row: IManagementSystemCertification) => { setEditing(row); setDialogOpen(true) }

  const submitHandler = async (values: IPostManagementSystemCertification) => {
    setIsSubmitting(true)
    try {
      if (editing) {
        await service.update(editing.id, values)
      } else {
        await service.create({ ...values, holderModelName, holderObjectId })
      }
      userNotificationsContext.openSnackbar({ open: true, message: editing ? 'Certificate updated' : 'Certificate added' })
      setDialogOpen(false)
      setEditing(null)
      await load()
      if (onChanged) onChanged()
    } catch (err: any) {
      console.error(err)
      userNotificationsContext.openSnackbar({
        open: true,
        message: err?.response?.data?.message || 'The certificate could not be saved. Please try again.',
        backgroundColor: 'text.primary',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const deleteHandler = (row: IManagementSystemCertification) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: `Remove the ${row.derived.label || 'certificate'}${row.certificateNumber ? ` (${row.certificateNumber})` : ''} from this record? Its dates leave your deadlines and any attached document is detached.`,
      buttonCopy: 'Remove certificate',
      cancelButtonCopy: 'Cancel',
    }).then(async (confirmed) => {
      if (!confirmed) return
      try {
        await service.remove(row.id)
        userNotificationsContext.openSnackbar({ open: true, message: 'Certificate removed' })
        await load()
        if (onChanged) onChanged()
      } catch (err: any) {
        console.error(err)
        userNotificationsContext.openSnackbar({
          open: true,
          message: err?.response?.data?.message || 'The certificate could not be removed. Please try again.',
          backgroundColor: 'text.primary',
        })
      }
    })
  }

  const expiresCell = (row: IManagementSystemCertification) => {
    const { derived } = row
    if (!derived.effectiveExpiresOn) return <Typography variant='body2' sx={{ color: 'text.secondary' }}>Not recorded</Typography>
    if (!derived.expiryCappedByTransition) return <Typography variant='body2'>{formatDate(derived.effectiveExpiresOn)}</Typography>
    const printed = derived.expiresOn ? `The printed expiry is ${formatLongDate(derived.expiresOn)}, but c` : 'No printed expiry is recorded, and c'
    return (
      <Tooltip title={`${printed}ertificates to this edition cease to be valid at the end of the ${derived.supersededBy} edition transition. Indicative date: confirm it with your certification body.`} placement='top'>
        <Typography variant='body2' sx={{ textDecoration: 'underline dotted', cursor: 'help' }}>{formatDate(derived.effectiveExpiresOn)}</Typography>
      </Tooltip>
    )
  }

  const statusCell = (row: IManagementSystemCertification) => {
    const { derived } = row
    return (
      <Stack direction='row' spacing={0.75} alignItems='center' flexWrap='wrap' useFlexGap>
        {certificationStatusChip(derived.status)}
        {derived.needsTransition && derived.status !== 'expired' && derived.transitionDeadline && (
          <Tooltip title='Indicative date. Confirm with your certification body.' placement='top'>
            <Chip size='small' color='warning' variant='outlined' label={`Move to the ${derived.supersededBy} edition by ${formatDate(derived.transitionDeadline)}`} />
          </Tooltip>
        )}
      </Stack>
    )
  }

  const headCells: IHeadCell[] = [
    { id: 'standard', numeric: false, label: 'Standard', sortable: true, sortValue: 'standardValue', isHeading: true, includeInMobile: true },
    { id: 'certificateNumber', numeric: false, label: 'Certificate number', sortable: false },
    { id: 'certificationBody', numeric: false, label: 'Certification body', sortable: false },
    { id: 'issuedOn', numeric: false, label: 'Issued', sortable: true, sortValue: 'issuedValue' },
    { id: 'expiresOn', numeric: false, label: 'Expires', sortable: true, sortValue: 'expiresValue', includeInMobile: true },
    ...(isOrganisation ? [{ id: 'nextAuditOn', numeric: false, label: 'Next audit', sortable: true, sortValue: 'auditValue' } as IHeadCell] : []),
    { id: 'status', numeric: false, label: 'Status', sortable: false, includeInMobile: true },
    { id: 'documents', numeric: false, align: 'center', label: 'Documents', sortable: false },
    ...(canEdit ? [{ id: 'action', numeric: false, align: 'right', label: '', sortable: false } as IHeadCell] : []),
  ]

  // An eye with the count when documents are attached (opens the viewer); the attach icon when
  // there are none and the reader may add one. A reader who cannot edit and has nothing to view
  // sees nothing.
  const documentsCell = (row: IManagementSystemCertification) => {
    if (row.documentCount > 0) {
      return (
        <Tooltip title={`View ${row.documentCount === 1 ? 'the document' : `${row.documentCount} documents`}`} placement='top'>
          <IconButton size='small' onClick={() => setDocumentsFor(row)} aria-label='View documents'>
            <Stack direction='row' spacing={0.5} alignItems='center'>
              <VisibilityOutlined fontSize='small' />
              <Typography variant='caption'>{row.documentCount}</Typography>
            </Stack>
          </IconButton>
        </Tooltip>
      )
    }
    if (!canEdit) return ''
    return (
      <Tooltip title='Attach the certificate' placement='top'>
        <IconButton size='small' onClick={() => setDocumentsFor(row)} aria-label='Attach a document' sx={{ color: 'text.secondary' }}>
          <AttachFileOutlined fontSize='small' />
        </IconButton>
      </Tooltip>
    )
  }

  const tableRows = rows.map((row) => ({
    id: row.id,
    standard: row.derived.label || row.standard,
    standardValue: row.derived.label || row.standard,
    certificateNumber: row.certificateNumber || '',
    certificationBody: row.certificationBody || '',
    issuedOn: formatDate(row.issuedOn),
    issuedValue: row.issuedOn || '',
    expiresOn: expiresCell(row),
    expiresValue: row.derived.effectiveExpiresOn || '',
    nextAuditOn: formatDate(row.nextAuditOn),
    auditValue: row.nextAuditOn || '',
    status: statusCell(row),
    documents: documentsCell(row),
    action: (
      <TableActionButtons
        id={row.id}
        includeEdit={true}
        includeDelete={true}
        onEdit={() => openEdit(row)}
        onDelete={() => deleteHandler(row)}
        additionalMenuItems={[{
          label: row.documentCount > 0 ? `Documents (${row.documentCount})` : 'Documents',
          icon: <AttachFileOutlined fontSize='small' />,
          onClick: () => setDocumentsFor(row),
        }]}
        preferViewThenAdditionalThenDelete={true}
      />
    ),
  }))

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <LoadingSpinner />
      </Box>
    )
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent='space-between' spacing={1} sx={{ mb: 2 }}>
        <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: 680 }}>
          {isOrganisation
            ? 'The ISO 14001 and ISO 50001 certificates your organisation holds. Expiry and audit dates appear in your deadlines, and the certificates are listed on the ISO 14001 evidence workbook. Recording a certificate here supports your evidence; it is not a certification.'
            : 'The ISO 14001 and ISO 50001 certificates this supplier holds. Their expiry dates appear in your deadlines and on the Suppliers sheet of the ISO 14001 evidence workbook.'}
        </Typography>
        {canEdit && (
          <Button variant='outlined' startIcon={<Add fontSize='small' />} onClick={openAdd} sx={{ whiteSpace: 'nowrap' }}>
            <Typography sx={{ pl: 0.5 }}>Add certificate</Typography>
          </Button>
        )}
      </Stack>
      {errorMessage && (
        <Stack direction='row' alignItems='center' spacing={2} sx={{ mb: 2 }}>
          <Typography variant='body2' color='error'>{errorMessage}</Typography>
          <Button variant='outlined' size='small' onClick={() => load()}>Try again</Button>
        </Stack>
      )}
      {!errorMessage && rows.length === 0 && (
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {isOrganisation
            ? 'Record your ISO 14001 or ISO 50001 certificate to see its expiry and audit dates in your deadlines.'
            : 'No certificates have been recorded for this supplier.'}
        </Typography>
      )}
      {rows.length > 0 && (
        <TableDisplay
          headCells={headCells}
          rows={tableRows}
          isMobileMode={isMobileMode}
          selected={selected}
          setSelected={setSelected}
          totalRecords={tableRows.length}
          perPage={perPage}
          setPerPage={setPerPage}
        />
      )}
      <AddEditCertificationDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditing(null) }}
        onSubmit={submitHandler}
        isSubmitting={isSubmitting}
        catalog={catalog}
        holderModelName={holderModelName}
        existing={editing}
      />
      <CertificationDocumentsDialog
        open={documentsFor !== null}
        onClose={() => { setDocumentsFor(null); load() }}
        certification={documentsFor}
        canEdit={canEdit}
      />
    </Box>
  )
}
